import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { getReportDetail, ReportDetail } from "../../../../services/subscription";
import RIcon from "../../../../components/RIcon";

const relevanceColor = (score: number) => {
  if (score >= 90) return "text-emerald-600 bg-emerald-50";
  if (score >= 75) return "text-amber-600 bg-amber-50";
  return "text-[#718096] bg-gray-100";
};

export default function ReportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [report, setReport] = useState<ReportDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setError("无效的报告 ID");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    getReportDetail(parseInt(id))
      .then((data) => {
        setReport(data);
      })
      .catch((err) => {
        console.error("Failed to load report detail:", err);
        setError("加载报告失败，请稍后重试");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <div className="p-6 min-h-full bg-gray-50/40 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-gray-100 rounded-full">
            <RIcon name="loader-4-line" className="text-xl text-[#718096] animate-spin" />
          </div>
          <p className="text-[#4A5568]">加载中...</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="p-6 min-h-full bg-gray-50/40 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-red-50 rounded-full">
            <RIcon name="error-warning-line" className="text-xl text-red-500" />
          </div>
          <p className="text-[#4A5568]">{error ?? "报告不存在"}</p>
          <Link to="/workbench/content-sub/reports" className="mt-4 inline-block text-sm text-[#0EA5A0] hover:underline">
            返回报告列表
          </Link>
        </div>
      </div>
    );
  }

  const allArticles = report.sections.flatMap(s => s.articles);
  const avgRelevance = allArticles.length > 0
    ? Math.round(allArticles.reduce((sum, a) => sum + a.relevance, 0) / allArticles.length)
    : 0;

  return (
    <div className="p-6 min-h-full bg-gray-50/40">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Breadcrumb / Back */}
        <div className="flex items-center gap-2 text-sm text-[#718096]">
          <Link to="/workbench/content-sub" className="hover:text-[#0EA5A0] cursor-pointer">内容订阅</Link>
          <RIcon name="arrow-right-s-line" />
          <Link to="/workbench/content-sub/reports" className="hover:text-[#0EA5A0] cursor-pointer">采集报告</Link>
          <RIcon name="arrow-right-s-line" />
          <span className="text-[#1A2B3C] font-medium truncate max-w-64">{report.title}</span>
        </div>

        {/* Report Header Card */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs bg-[#0EA5A0]/10 text-[#0EA5A0] px-2.5 py-0.5 rounded-full font-medium">{report.subscription_name}</span>
                <span className="text-xs bg-emerald-50 text-emerald-600 px-2.5 py-0.5 rounded-full font-medium flex items-center gap-1">
                  <div className="w-3 h-3 flex items-center justify-center"><RIcon name="check-circle-line" /></div>
                  已完成
                </span>
              </div>
              <h1 className="text-xl font-bold text-[#1A2B3C] leading-snug">{report.title}</h1>
              <div className="flex flex-wrap gap-4 mt-3 text-sm text-[#718096]">
                <span className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center"><RIcon name="time-line" /></div>
                  {report.created_at}
                </span>
                {report.duration && (
                  <span className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center"><RIcon name="timer-line" /></div>
                    耗时 {report.duration}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {report.file_id && (
                <Link to={`/workbench/files?id=${report.file_id}`} className="flex items-center gap-1.5 px-4 h-9 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                  <div className="w-4 h-4 flex items-center justify-center"><RIcon name="folder-line" /></div>
                  文件库
                </Link>
              )}
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-4 gap-4 mt-5 pt-5 border-t border-gray-100">
            {[
              { label: "采集网站", value: report.sources_count, unit: "个", icon: "ri-global-line", color: "text-[#0EA5A0]" },
              { label: "有效文章", value: report.articles_count, unit: "篇", icon: "ri-article-line", color: "text-blue-500" },
              { label: "命中关键词", value: report.keywords.length, unit: "个", icon: "ri-key-2-line", color: "text-violet-500" },
              { label: "平均相关度", value: `${avgRelevance}%`, unit: "", icon: "ri-bar-chart-line", color: "text-amber-500" },
            ].map(stat => (
              <div key={stat.label} className="text-center">
                <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}<span className="text-sm font-normal text-[#718096]">{stat.unit}</span></div>
                <div className="text-xs text-[#718096] mt-0.5 flex items-center justify-center gap-1">
                  <div className="w-3.5 h-3.5 flex items-center justify-center"><i className={stat.icon} /></div>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Meta Info */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-xs font-medium text-[#718096] mb-2 flex items-center gap-1">
              <div className="w-3.5 h-3.5 flex items-center justify-center"><RIcon name="global-line" /></div>
              采集网站
            </p>
            <div className="flex flex-wrap gap-1.5">
              {report.websites.map(w => (
                <span key={w} className="text-xs bg-gray-100 text-[#4A5568] px-2.5 py-1 rounded-md">{w}</span>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-xs font-medium text-[#718096] mb-2 flex items-center gap-1">
              <div className="w-3.5 h-3.5 flex items-center justify-center"><RIcon name="key-2-line" /></div>
              监控关键词
            </p>
            <div className="flex flex-wrap gap-1.5">
              {report.keywords.map(kw => (
                <span key={kw} className="text-xs bg-[#0EA5A0]/10 text-[#0EA5A0] px-2.5 py-1 rounded-full">{kw}</span>
              ))}
            </div>
          </div>
        </div>


        {report.markdown && (
          <div className="bg-white rounded-xl border border-gray-100 p-5 overflow-hidden">
            <h2 className="text-base font-semibold text-[#1A2B3C] mb-3">Markdown 报告</h2>
            <div className="prose prose-sm max-w-none text-[#4A5568] break-words [&_a]:break-all [&_a]:text-[#0EA5A0] [&_a]:hover:underline [&_code]:break-all [&_pre]:overflow-x-auto [&_pre]:whitespace-pre-wrap">
              <ReactMarkdown>{report.markdown}</ReactMarkdown>
            </div>
          </div>
        )}

        {/* Content Sections */}
        <div className="space-y-4">
          <h2 className="text-base font-semibold text-[#1A2B3C]">报告正文</h2>
          {report.sections.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 py-12 text-center">
              <p className="text-[#718096] text-sm">暂无内容</p>
            </div>
          ) : report.sections.map(section => (
            <div key={section.keyword} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50/50 cursor-pointer transition-colors"
                onClick={() => setActiveSection(activeSection === section.keyword ? null : section.keyword)}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xs bg-[#0EA5A0]/10 text-[#0EA5A0] px-2.5 py-1 rounded-full font-medium">{section.keyword}</span>
                  <span className="text-sm text-[#4A5568]">{section.articles.length} 篇相关文章</span>
                </div>
                <div className="w-5 h-5 flex items-center justify-center text-[#718096]">
                  <i className={`ri-arrow-${activeSection === section.keyword ? "up" : "down"}-s-line`} />
                </div>
              </button>

              {(activeSection === section.keyword || activeSection === null) && (
                <div className="divide-y divide-gray-50 border-t border-gray-100">
                  {section.articles.map((article) => (
                    <div key={article.id} className="px-5 py-4 hover:bg-gray-50/30 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1.5">
                            <a
                              href={article.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-semibold text-[#1A2B3C] hover:text-[#0EA5A0] leading-snug transition-colors"
                            >
                              {article.title}
                            </a>
                          </div>
                          {article.excerpt && (
                            <p className="text-sm text-[#4A5568] leading-relaxed line-clamp-3">{article.excerpt}</p>
                          )}
                          <div className="flex items-center gap-3 mt-2.5 text-xs text-[#718096]">
                            <span className="flex items-center gap-1">
                              <div className="w-3.5 h-3.5 flex items-center justify-center"><RIcon name="global-line" /></div>
                              {article.source}
                            </span>
                            {article.published_at && (
                              <span className="flex items-center gap-1">
                                <div className="w-3.5 h-3.5 flex items-center justify-center"><RIcon name="calendar-line" /></div>
                                {article.published_at}
                              </span>
                            )}
                            <a
                              href={article.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 hover:text-[#0EA5A0] transition-colors"
                            >
                              <div className="w-3.5 h-3.5 flex items-center justify-center"><RIcon name="external-link-line" /></div>
                              原文链接
                            </a>
                          </div>
                        </div>
                        <div className="shrink-0">
                          <span className={`text-xs font-medium px-2 py-1 rounded-lg ${relevanceColor(article.relevance)}`}>
                            {article.relevance}%
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer note */}
        <div className="bg-amber-50 border border-amber-100 rounded-xl px-5 py-3 flex items-start gap-3 text-sm text-amber-700">
          <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0"><RIcon name="information-line" /></div>
          <span>本报告由系统自动采集生成，内容仅供参考。相关度得分基于关键词匹配度和语义相似度综合计算，建议结合实际情况判断。</span>
        </div>

        <div className="pb-6" />
      </div>
    </div>
  );
}
